import _ from 'lodash';
import React from 'react';

import expansionOpts from 'shared/expansionOptions';
import GameData from 'shared/gameData';

export const simcVersions = [{
  value: 'release',
  name: 'Release',
  description: 'Latest official release',
  public: false,
}, {
  value: 'weekly',
  name: 'Weekly',
  description: 'Built from source every Monday',
  default: true,
  public: true,
}, {
  value: 'nightly',
  name: 'Nightly',
  description: 'Nightly build from source, most up-to-date but low chance of bugs',
  public: true,
}, {
  value: 'beta',
  name: 'Beta',
  description: 'Beta build',
  public: false,
}, {
  value: 'latest',
  name: 'Latest',
  description: (
    'The latest build from SimC. Can change rapidly. Higher chance of bugs.' +
    'Keep an eye on the git hash to avoid confusion.'
  ),
  public: true,
}];

export const SMART_SIM_VALUE = 'smart';
export const SMART_SIM_ITERATIONS = 5000;

export const isSmartIterations = iterations => iterations === SMART_SIM_VALUE;

export const getIterations = (iterations) => {
  return isSmartIterations(iterations) ? SMART_SIM_ITERATIONS : iterations;
};

const iterationOptionsInternal = [{
  value: 'smart',
  name: 'Smart Sim',
  description: (
    <span>
      Recommended - Raidbots chooses iterations and target error for you. Generally should not be changed.
      {' '}
      <a
        href="https://support.raidbots.com/article/55-smart-sim"
        target="_blank"
        rel="noopener noreferrer"
      >
        More info
      </a>
    </span>
  ),
  public: true,
  default: true,
}, {
  value: 5000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 10000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 15000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 20000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 25000,
  public: true,
  description: 'Not recommended unless you are an advanced SimC user',
}, {
  value: 30000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 40000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 50000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 60000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 70000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 80000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 90000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}, {
  value: 100000,
  description: 'Target error will be capped at 0.05%',
  public: true,
}];

if (process.env.NODE_ENV === 'development') {
  iterationOptionsInternal.unshift({
    value: 2499,
    public: true,
    description: 'DEV ONLY',
  });
  iterationOptionsInternal.unshift({
    value: 999,
    public: true,
    description: 'DEV ONLY',
  });
  iterationOptionsInternal.unshift({
    value: 99,
    public: true,
    description: 'DEV ONLY',
  });
}

export const iterationOptions = iterationOptionsInternal;

export const fightStyles = [{
  value: 'Patchwerk',
  name: 'Patchwerk',
  description: 'No movement, single target',
  public: true,
  default: true,
}, {
  value: 'DungeonSlice',
  name: 'Dungeon Slice',
  description: (
    <span>
      6 minute slice of an M+ dungeon.  Overrides Fight Length.
      Check with your class Discord to see if appropriate for your spec.
    </span>
  ),
  public: true,
  overrides: {
    enemyCount: 1,
    fightLength: 360,
    balefireBranchRngType: 'rppm',
  },
  unsupportedSpecs: [
    102, // Balance Druid
    577, // Havoc DH
    581, // Vengeance DH
  ],
}, {
  value: 'TargetDummy',
  name: 'Target Dummy',
  description: 'Always 100% health, raid buffs/debuffs disabled, no consumables',
  public: true,
  overrides: {
    fightStyle: null,
    bloodlust: 0,
    arcaneIntellect: 0,
    fortitude: 0,
    battleShout: 0,
    mysticTouch: 0,
    chaosBrand: 0,
    skyfury: 0,
    markOfTheWild: 0,
    huntersMark: 0,
    powerInfusion: 0,
    bleeding: 0,
    potion: 'disabled',
    food: 'disabled',
    flask: 'disabled',
    augmentation: 'disabled',
    temporaryEnchant: 'disabled',
    enemies: [{
      name: 'Target Dummy',
      properties: {
        enemy_fixed_health_percentage: 100,
      },
    }],
    customInput: [
      ['dragonflight.voice_of_the_silent_star_enable', 0],
    ],
  },
  disableAdvanced: true,
}, {
  value: 'ExecutePatchwerk',
  name: 'Execute Patchwerk',
  description: 'Target starts at 20% HP',
  public: true,
  overrides: {
    fightStyle: null,
    enemies: [{
      name: 'Execute Range',
      properties: {
        enemy_initial_health_percentage: 20,
      },
    }],
  },
  disableAdvanced: true,
}, {
  value: 'HecticAddCleave',
  name: 'Hectic Add Cleave',
  description: 'Regular add spawns (5 adds up about 40% of the fight), frequent movement. Similar to T15 Horridon',
  public: true,
}, {
  value: 'LightMovement',
  name: 'Light Movement',
  description: 'Infrequent movement - Move 50 yards every 85 seconds',
  public: true,
}, {
  value: 'HeavyMovement',
  name: 'Heavy Movement',
  description: 'Frequent movement - Move 25 yards every 10 seconds',
  public: true,
}, {
  value: 'CastingPatchwerk',
  name: 'Casting Patchwerk',
  description: 'Patchwerk that is always casting, can be useful for simming interrupts',
  public: true,
}, {
  value: 'CleaveAdd',
  name: 'Cleave Add',
  description: 'Single add is up about 60% of the fight',
  public: true,
}, {
  value: 'HelterSkelter',
  name: 'Helter Skelter',
  description: 'Movement every 30 seconds, interrupts every 30 seconds, boss invulnerable every 120 seconds',
  public: false,
}, {
  value: 'Beastlord',
  name: 'Beastlord',
  description: 'Fight with a lot of various add packs up most of the fight. Similar to T17 Beastlord Darmac',
  public: false,
}, {
  value: 'Ultraxion',
  name: 'Ultraxion',
  description: 'Single target fight with player stuns and damage to the raid',
  public: false,
}];
export const fightStylesLower = _.mapValues(fightStyles, style => ({
  ...style,
  value: _.toLower(style.value),
}));

const fightLengthDescription = 'By default, fight length will vary up to 20% for each iteration';

export const fightLengths = [{
  value: 20,
  name: '20 seconds',
  description: fightLengthDescription,
  public: true,
}, {
  value: 40,
  name: '40 seconds',
  description: fightLengthDescription,
  public: true,
}, {
  value: 60,
  name: '1 minute',
  public: true,
}, {
  value: 90,
  name: '1 minute, 30 seconds',
  description: fightLengthDescription,
  public: true,
}, {
  value: 120,
  name: '2 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 180,
  name: '3 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 240,
  name: '4 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 300,
  name: '5 minutes',
  description: fightLengthDescription,
  public: true,
  default: true,
}, {
  value: 360,
  name: '6 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 420,
  name: '7 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 480,
  name: '8 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 540,
  name: '9 minutes',
  description: fightLengthDescription,
  public: true,
}, {
  value: 600,
  name: '10 minutes',
  description: fightLengthDescription,
  public: true,
}];

export const enemyCounts = [{
  value: 1,
  name: '1 Boss',
  description: 'Default and best for single target sims',
  default: true,
}, {
  value: 2,
  name: '2 Bosses',
  description: 'Two stacked bosses up at all times, good for multitarget sims',
}, {
  value: 3,
  name: '3 Bosses',
  description: 'Three stacked bosses up at all times, good for multitarget sims',
}, {
  value: 4,
  name: '4 Bosses',
  description: '',
}, {
  value: 5,
  name: '5 Bosses',
  description: '',
}, {
  value: 6,
  name: '6 Bosses',
  description: '',
}, {
  value: 7,
  name: '7 Bosses',
  description: '',
}, {
  value: 8,
  name: '8 Bosses',
  description: '',
}, {
  value: 9,
  name: '9 Bosses',
  description: '',
}, {
  value: 10,
  name: '10 Bosses',
  description: '',
}];

export const enemyTypes = [{
  value: 'FluffyPillow',
  name: 'Fluffy Pillow',
  description: 'Default boss type, best for most DPS simulations',
  default: true,
  public: true,
}];

// populate most of this from game data
export const getFoods = _.memoize(() => {
  return [{
    value: '',
    name: 'SimC Default',
    default: true,
  },
  ...GameData.Foods,
  {
    value: 'disabled',
    name: 'No Food',
    shortName: 'No Food',
  }];
});

export const getPotions = _.memoize(() => {
  return [{
    value: '',
    name: 'SimC Default',
    default: true,

  },
  ...GameData.Potions,
  {

    value: 'disabled',
    name: 'No Potion',
    shortName: 'No Potion',
  }];
});

export const getFlasks = _.memoize(() => {
  return [{
    value: '',
    name: 'SimC Default',
    default: true,

  },
  ...GameData.Flasks,
  {
    value: 'disabled',
    name: 'No Flask',
    shortName: 'No Flask',
  }];
});

export const getAugments = _.memoize(() => {
  return [{
    value: '',
    name: 'SimC Default',
    default: true,
  },
  ...GameData.Augments,
  {
    value: 'disabled',
    name: 'Disabled',
    shortName: 'No Rune',
  }];
});

export const expansionOptions = expansionOpts;
